export interface BoxData {
  id: number;
  name: string;
  description: string;
  sortOrder: number;
  purchaseCost: number;
  shippingCost: number;
  storageCost: number;
  count: number;
  imageUrl: string;
  dimensions: Dimensions;
  returnFee: boolean;
  chargeShipping: boolean;
}

export interface Dimensions {
  depth: number;
  height: number;
  width: number;
  weight: number;
}

export interface Discount {
  deletedAt: number;
  createdAt: number;
  updatedAt: number;
  discountCode: string;
  discountPercentage: number;
  expires: number;
  recurring: boolean;
  upFront: boolean;
  id: number;
}

export interface DiscountAndCount extends Discount {
  count: number;
}

export interface MakeOrderResponse {
  order: Order;
  paymentIntentSecret?: string;
  setupIntentSecret?: string;
  jwtSecret: string;
  paymentStatus: PaymentStatus;
}

export enum PaymentStatus {
  succeeded = 'succeeded',
  requiresAction = 'requires_action',
  requiresPaymentMethod = 'requires_payment_method',
  trialing = 'trialing',
  noneNeeded = 'none_needed',
  duplicatePrevented = 'duplicate_prevented',
}

export interface MakePaymentRequest {
  orderId: number;
  paymentMethodId: string;
}

export interface UpdatePaymentRequest {
  paymentMethodId: string;
}

export interface MakePaymentResponse {
  session: string;
}

export interface BoxCount {
  [key: number]: number;
}

export interface BookingParams {
  route: string;
}

export interface MakeOrderRequest {
  city?: string;
  boxes: BoxCount;
  collectionAddress: string;
  deliveryAddress?: string;
  collectionDate: number;
  deliveryDate?: number;
  roomPack: boolean;
  storageRequired: boolean;
  discountCode?: string;
  note: string;
  // Card and Apple Pay create the order with a Stripe payment-method id.
  // Klarna is confirmed after a redirect, so the order is created with the
  // method type and no id — modelled here rather than sidestepped with
  // `any`, which is how the Klarna path used to build this request.
  paymentMethodId?: string;
  paymentMethodType?: 'klarna';
  collectionCountry?: String;
  deliveryCountry?: String;
  collectionPostcode?: String;
  deliveryPostcode?: String;
}

export interface MakeSuitcaseOrderRequest {
  city?: string;
  collectionAddress: string;
  deliveryAddress?: string;
  collectionDate: number;
  deliveryDate?: number;
  suitcases: Dimensions[];
  discountCode?: string;
  note: string;
  // Card and Apple Pay create the order with a Stripe payment-method id.
  // Klarna is confirmed after a redirect, so the order is created with the
  // method type and no id — modelled here rather than sidestepped with
  // `any`, which is how the Klarna path used to build this request.
  paymentMethodId?: string;
  paymentMethodType?: 'klarna';
  country?: String;
  collectionCountry?: String;
  deliveryCountry?: String;
  collectionPostcode?: String;
  deliveryPostcode?: String;
}

export interface Order extends MakeOrderRequest {
  id: number;
  createdAt: number;
  updatedAt: number;
  deletedAt?: number;
  payments: PaymentResponse[];
  statuses: StatusResponse[];
  customer: UserResponse;
  orderBoxes: OrderBoxesResponse[];
  initialCost: number;
  monthlyCost: number;
  customerId: number;
  orderComplete: boolean;
  roomPackIncluded?: boolean;
  suitcaseOrder: boolean;
  notes: Note[];
  collectionCountry?: String;
  deliveryCountry?: String;
  collectionPostcode?: String;
  deliveryPostcode?: String;

  // Logistics assignment fields
  assignedDriverId?: number | null;       // legacy – kept for backward compat
  collectionDriverId?: number | null;     // driver for the collection leg
  deliveryDriverId?: number | null;       // driver for the delivery leg
  assignedPartnerId?: number | null;
  assignedWarehouseId?: number | null;

  // True when this order predates the new box-tracking flow and should render
  // the master-style card without the per-stage journey. Optional; absent on
  // master backend, populated on feature/logistics-and-models post-migration.
  legacyTracking?: boolean;

  // Proof photo fields
  collectionProofPhotoUrl?: string | null;
  deliveryProofPhotoUrl?: string | null;

  // Removal order fields (optional for regular orders)
  orderType?: 'removal';
  tierId?: string;
  tierName?: string;
  tierPrice?: number;
  propertyFloor?: string;
  propertyHasLift?: boolean | null;
  propertyHasFurniture?: boolean;
  propertyHasParking?: boolean;
  collectionCity?: string;
  deliveryCity?: string;
  moveDate?: number;
  packingMaterialsId?: string | null;
  packingMaterialsPrice?: number | null;
  packingMaterialsDetails?: string | null;
  storageId?: string | null;
  storageMonthlyPrice?: number | null;
  storageSize?: string | null;
  baseCost?: number;
  extrasCost?: number;
  totalInitialCost?: number;
  monthlyStorageCost?: number;
  discountPercentage?: number | null;
  discountApplied?: number;
  status?: string;
  stripePurchaseId?: string | null;
  stripeSubscriptionId?: string | null;
}

export interface Note {
  id: number;
  orderId: number;
  text: string;
  updatedAt: number;
  createdAt: number;
}

export interface OrderResponse {
  order: Order;
}

export interface OrderListResponse {
  orders: Order[];
}

export interface PreviousOrderListResponse {
  previousOrders: Order[];
  nextPayments?: { [orderId: number]: NextPaymentResponse };
}

export interface RemovalOrdersResponse {
  statusCode: number;
  orders: Order[];
  nextPayments?: { [orderId: number]: NextPaymentResponse };
}

export interface RemovalQuoteRequest {
  id: number;
  customerId?: number;
  tierType: string;
  tierName: string;
  customerEmail: string;
  customerPhone: string;
  message: string;
  status: string;
  quotedPrice?: number | null;
  quotedAt?: number | null;
  quotedByUserId?: number | null;
  createdAt: number;
  updatedAt: number;
  customer?: Customer;
  itemType?: 'quote'; // Helper to identify in merged lists
}

export interface RemovalQuoteRequestsResponse {
  statusCode: number;
  quotes: RemovalQuoteRequest[];
}

export interface StatusResponse {
  id: number;
  orderId: number;
  customerId: number;
  status: { id: number; type: string }; //Do this rather than make a new model
  statusId: number;
  updatedAt: number;
}

export interface OrderBoxesResponse {
  id: number;
  barcode: string;
  boxId: number;
  orderId: number;
  box: BoxData;
  dimensions: Dimensions;
  shippingCost: number;
}

export interface PaymentResponse {
  id: number;
  orderId: number;
  customerId: number;
  amount: number;
  succeeded: boolean;
  createdAt: number;
  stripeId?: string;
  status: string;
}

export interface Customer {
  orders: Order[];
  payments?: PaymentResponse[];
  id: number;
  createdAt: number;
  updatedAt: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  stripeId: string;
  phoneNumber?: string;
}

export interface UserResponse {
  id: number;
  createdAt: number;
  updatedAt: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  admin?: boolean;
  role?: string;
}

export interface MyAccountResponse {
  totalBoxDays: number;
  membershipTier?: string;
  jwt: string;
  customer: {
    id: number;
    createdAt: number;
    updatedAt: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    stripeId?: string;
    hubspotId?: number;
    orders?: Order[];
    payments?: PaymentResponse[];
  };
}

export interface CustomerListResponse {
  customers: Customer[];
}

export interface CustomerResponse {
  customer: Customer;
}

export interface UserListResponse {
  users: UserResponse[];
}

export interface DiscountResponse {
  discount: Discount;
}

export interface ProductResponse {
  product: Product;
}

export interface DiscountListResponse {
  discounts: DiscountAndCount[];
}

export interface ProductAndCount extends Product {
  count: number;
}

export interface ProductListResponse {
  products: ProductAndCount[];
}

export interface Product extends BoxData {
  createdAt: number;
  emailField: string;
  updatedAt: number;
}

export interface CheckoutProduct {
  name?: string;
  id?: number;
  price: string;
  brand: string;
  category: string;
  quantity: number;
  affiliation: string;
  coupon?: string;
  discount?: string;
  currency?: string;
}

export interface CheckoutItem {
  item_id?: string;
  item_name: string;
  affiliation: string;
  coupon?: string;
  currency: string;
  discount?: string;
  index: number;
  item_brand: string;
  item_category: string;
  price: string;
  quantity: string;
}

/**
 * The UK bank-holiday calendar, as served by `GET /bank-holidays`.
 *
 * The shape is gov.uk's, because that is where this list used to be fetched from — directly
 * from the browser, until 2026-09-27. Everything but `date` is optional: gov.uk sends
 * `title`, `notes`, `bunting` and `division` on every event, storage-api holds only the
 * dates, and nothing in this repo has ever read the rest.
 */
export interface BankHolidays {
  'england-and-wales': BankHolidayDivision;
  'northern-ireland': BankHolidayDivision;
  scotland: BankHolidayDivision;
}

interface BankHolidayDivision {
  division?: string;
  events: BankHoliday[];
}

export interface BankHoliday {
  date: string;
  bunting?: boolean;
  notes?: string;
  title?: string;
}

export enum BoxChangeType {
  ADDED = 'ADDED',
  REMOVED = 'REMOVED',
}

export enum BoxChangeSource {
  HUBSPOT = 'HUBSPOT',
  SYSTEM = 'SYSTEM',
  MANUAL = 'MANUAL',
}

export interface BoxChange {
  id: number;
  customerId: number;
  orderId: number;
  orderBoxId?: number;
  boxId: number;
  changeType: BoxChangeType;
  boxName: string;
  barcode?: string;
  source: BoxChangeSource;
  reason?: string;
  changedBy?: string;
  createdAt: number;
  updatedAt: number;
}

export interface BoxChangesResponse {
  statusCode: number;
  boxChanges: BoxChange[];
}

export interface NextPaymentResponse {
  statusCode: number;
  nextPaymentDate: number;
  amount: number;
  currency: string;
  status: string;
}

export interface OxfordPackageOrder {
  id: number;
  college: string;
  packageId: string;
  packageName: string;
  packagePrice: number;
  paymentStatus: string;
  billingLine1: string;
  billingCity: string;
  billingPostcode: string;
  billingCountry: string;
  customerId: number;
  createdAt: number;
  updatedAt: number;
}

export interface OxfordPackageOrdersResponse {
  orders: OxfordPackageOrder[];
  jwt: string;
}

export interface PartnerUser {
  id: number;
  name?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  phoneNumber?: string;
  partnerId: number;
  role?: string;
  isActive?: boolean;
}

export interface Partner {
  id: number;
  name?: string;
  companyName?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export interface Warehouse {
  id: number;
  name: string;
  city: string;
  postcode: string;
  address?: string;
  partnerId?: number;
  isActive?: boolean;
}

export interface AssignLogisticsRequest {
  courierId?: number;
  collectionCourierId?: number;
  deliveryCourierId?: number;
  warehouseId?: number;
}

export interface OrderAssignmentResponse {
  order: Order;
  courier?: PartnerUser;
  collectionCourier?: PartnerUser;
  deliveryCourier?: PartnerUser;
  courierPartner?: Partner;
  warehouse?: Warehouse;
}

export interface AvailableCouriersResponse {
  couriers: PartnerUser[];
}

export interface WarehousesResponse {
  warehouses: Warehouse[];
}

export interface PartnerUser {
  id: number;
  name?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  phoneNumber?: string;
  partnerId: number;
  role?: string;
  isActive?: boolean;
}

// DPD Shipping interfaces
export interface BackendShippingAddress {
  contactName: string;
  organisationName: string;
  addressLine1: string;
  addressLine2: string;
  town: string;
  county: string;
  postcode: string;
  countryCode: string;
  phone: string;
  email: string;
}

export interface CreateDPDLabelRequest {
  orderId: number;
  collectionAddress: BackendShippingAddress;
  deliveryAddress: BackendShippingAddress;
  numberOfParcels: number;
  totalWeight: number;
  networkCode: string;
  collectionDate: string;
  shippingRef1: string;
  deliveryInstructions: string;
}

export interface DPDLabel {
  id: number;
  orderId: number;
  carrier: string;
  trackingNumber: string;
  parcelNumber: string;
  shipmentId: string;
  labelFormat: string;
  status: string;
  createdByUserId: number;
  createdAt: number;
  updatedAt: number;
  labelData: string;
  dpdTrackingStatus: string | null;
  dpdTrackingUpdatedAt: number | null;
  dpdPodImageKey: string | null;
  dpdPodName: string | null;
  shipmentType: string;
  collectionCode: string | null;
}

export interface CreateDPDLabelResponse {
  label: DPDLabel;
  jwt: string;
}

export interface ListDPDLabelsResponse {
  labels: DPDLabel[];
  jwt: string;
}

export interface DPDService {
  networkCode: string;
  networkDescription: string;
  notifyRequired: string;
  insurance: boolean;
  collectOnDelivery: boolean;
}

export interface ListDPDServicesResponse {
  services: DPDService[];
  jwt: string;
}

export interface VoidDPDLabelResponse {
  label: DPDLabel;
  jwt: string;
}

export interface DPDTrackingEvent {
  date: string;
  time: string;
  eventKey: string;
  eventType: string;
  eventStatus: string;
  locality: string;
}

export interface DPDTrackingData {
  parcelCode: string;
  parcelNumber: string;
  currentStatus: string;
  deliveryStatus: string;
  parcelStatus: string;
  deliveredToName: string | null;
  deliveryDate: string | null;
  events: DPDTrackingEvent[];
  deliveryDetails: string | null;
}

export interface DPDTrackingResponse {
  tracking: DPDTrackingData;
  jwt: string;
}

export interface DPDPodImageResponse {
  imageBase64: string;
  jwt: string;
}

export interface CreateDPDCollectionResponse {
  collection: DPDLabel;
  jwt: string;
}

export interface CancelDPDCollectionResponse {
  collection: DPDLabel;
  jwt: string;
}

export interface DPDCollectionDate {
  date: string;
  firstInArea: string;
  lastInArea: string;
  collectionCutOff: string;
}

export interface DPDCollectionDatesResponse {
  dates: DPDCollectionDate[];
  jwt: string;
}

// Logistics Admin Order types
export interface AdminOrderRow {
  id: number;
  type: string;
  date: string;
  isoDate: string;
  customer: string;
  address: string;
  postcode: string;
  boxes: number;
  partnerId: number | null;
  partnerName: string | null;
  driverId: number | null;
  driverName: string | null;
  collectionDriverId: number | null;
  collectionDriverName: string | null;
  deliveryDriverId: number | null;
  deliveryDriverName: string | null;
  warehouseName: string | null;
  collectionEta: string | null;
  deliveryEta: string | null;
  collectionEtaStart?: string | null;
  collectionEtaEnd?: string | null;
  deliveryEtaStart?: string | null;
  deliveryEtaEnd?: string | null;
  timeslot: string | null;
  doorstep: boolean;
  lastScan: string | null;
  routeLocked: boolean;
}

export interface AdminOrdersResponse {
  statusCode: number;
  orders: AdminOrderRow[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface SetETARequest {
  collectionEta?: string;
  deliveryEta?: string;
  collectionEtaStart?: string;
  collectionEtaEnd?: string;
  deliveryEtaStart?: string;
  deliveryEtaEnd?: string;
}

export interface AssignOrderRequest {
  driverId?: number;
  collectionEta?: string;
  deliveryEta?: string;
  collectionEtaStart?: string;
  collectionEtaEnd?: string;
  deliveryEtaStart?: string;
  deliveryEtaEnd?: string;
  partnerId?: number;
}

export interface ToggleLogisticsControlRequest {
  canControlLogistics: boolean;
}

export interface AdminOrderAssignmentResponse {
  order: AdminOrderRow;
}

// Partner User Access types
export interface PartnerUserAccess {
  id: number;
  name: string;
  email: string;
  role: string;
  partnerId: number;
  partnerName: string;
  isActive: boolean;
  logisticsDashboardEnabled: boolean;
  canControlLogistics?: boolean;
}

export interface PartnerUserAccessListResponse {
  statusCode: number;
  users: PartnerUserAccess[];
}

export interface ToggleAccessResponse {
  statusCode: number;
  userId: number;
  logisticsDashboardEnabled: boolean;
}

// Time-slot booking types
export type TimeSlotValue = 'pre_12' | 'post_12';

export interface TimeslotResponse {
  orderId: number;
  timeSlot: TimeSlotValue;
  paymentIntentClientSecret?: string;
  jwt: string;
}

export interface TimeslotListResponse {
  timeslots: { orderId: number; timeSlot: TimeSlotValue }[];
}

export interface HasPaymentMethodResponse {
  hasPaymentMethod: boolean;
}

// ─── Return-delivery time-slot types (v2.17) ────────────────
// Backend: storage-api ReturnTimeslotRepository / ReturnTimeslotEligibilityService.
//
// Eligibility is ENTIRELY server-side and must stay that way. It depends on the
// warehouse, the assigned return courier and an Ops-editable matrix, none of which
// the browser has. Render what the API returns; never re-derive it here, and do not
// reuse the client-side `isWithinWorkingDays` filter the collection modal applies.

/** Why the option isn't on offer. Mirrors the backend enum. */
export type ReturnTimeslotIneligibilityReason =
  | 'NO_DELIVERY_DATE'
  | 'ORDER_CANCELLED'
  | 'ALREADY_BOOKED'
  | 'TOO_LATE'
  | 'TOO_EARLY'
  | 'AREA_NOT_ELIGIBLE'
  | 'COURIER_NOT_ELIGIBLE'
  | 'NATIONAL_CARRIER'
  | 'NO_SLOTS_ENABLED';

export interface ReturnTimeslotOption {
  slot: TimeSlotValue;
  /** Pence. 0 is a real, free option — not "unpriced". */
  feePence: number;
}

export interface ReturnTimeslotSelection {
  slot: TimeSlotValue;
  feePence: number;
  paymentStatus: string;
  bookedAt: number;
}

export interface ReturnTimeslotView {
  orderId: number;
  eligible: boolean;
  reason?: ReturnTimeslotIneligibilityReason;
  areaLabel?: string;
  options: ReturnTimeslotOption[];
  selected?: ReturnTimeslotSelection;
}

export interface ReturnTimeslotViewResponse {
  returnTimeslot: ReturnTimeslotView;
}

export interface ReturnTimeslot {
  id: number;
  orderId: number;
  customerId: number;
  timeSlot: TimeSlotValue;
  amount: number;
  ruleId?: number;
  stripePaymentIntentId?: string;
  paymentStatus: string;
  createdAt: number;
  updatedAt: number;
}

export interface ReturnTimeslotPurchaseResponse {
  timeslot: ReturnTimeslot;
  /** Present only when the card needs 3DS. */
  paymentIntentClientSecret?: string;
  jwt: string;
}

export interface ReturnTimeslotListResponse {
  returnTimeslots: ReturnTimeslot[];
}

/** Only the currently-eligible orders, for the dashboard call-to-action. */
export interface ReturnTimeslotEligibleListResponse {
  eligible: ReturnTimeslotView[];
}

// ─── Logistics v2.5+ types ─────────────────────────────────
// Backend: storage-api feature/logistics-and-models
// Handoff: see /Users/mohanadamrbarakat/.claude/plans/backend-handoff-delightful-thacker.md

export type LogisticsUserRole = 'MASTER' | 'DRIVER' | 'WAREHOUSE_STAFF';

export interface CreateLogisticsUserRequest {
  email: string;
  name: string;
  role: LogisticsUserRole;
  phone?: string;
  password: string;
  logisticsDashboardEnabled: boolean;
}

// PATCH-style update for an existing logistics user. All fields optional;
// `password` (when set) is a NEW password the admin is rotating to. Server
// hashes it with the same BCrypt cost the scanner login expects.
export interface UpdateLogisticsUserRequest {
  email?: string;
  name?: string;
  role?: LogisticsUserRole;
  phone?: string | null;
  password?: string;
  isActive?: boolean;
  logisticsDashboardEnabled?: boolean;
  canControlLogistics?: boolean;
}

// Pricing — §1 Phase 1 + §3.1, §3.2, §3.3
// Exact RateType values come from the backend RateType enum; kept as string to
// avoid client/server drift. Known values include COURIER_* and WH_* families
// plus FAILED_DELIVERY_FEE / WH_FAILED_DELIVERY_FEE.
export type RateType = string;

export type ApplyMode = 'FLAT' | 'PER_BOX' | 'PER_DELIVERY' | 'WEEKLY';

export interface BillingRateDTO {
  rateType: RateType;
  rateAmount: number;
  warehouseId: number | null;
}

export interface PartnerAdditionalFeeDTO {
  label: string;
  amountPence: number;
  applyMode: ApplyMode;
}

export interface PartnerPricingRequest {
  rates: BillingRateDTO[];
  additionalFees?: PartnerAdditionalFeeDTO[];
}

export interface PartnerPricingResponse {
  rates: BillingRateDTO[];
  additionalFees: PartnerAdditionalFeeDTO[];
}

// Invoices — §1 Phase 1 + §3.1, §3.2
export interface BoxRateDTO {
  boxes: number;
  rate: number;
}

export interface FailedDeliveriesDTO {
  count: number;
  rate: number;
}

export interface InvoiceAdditionalFeeDTO {
  label: string;
  quantity: number;
  rate: number;
  applyMode?: ApplyMode;
}

export interface InvoiceWeekDTO {
  weekCommencing: string;
  displayLabel: string;
  doorstepCollections: BoxRateDTO;
  timeslotCollections: BoxRateDTO;
  doorstepReturns: BoxRateDTO;
  timeslotReturns: BoxRateDTO;
  minCharge: number;
  failedDeliveries: FailedDeliveriesDTO | null;
  additionalFees: InvoiceAdditionalFeeDTO[];
}

// Order issues — dashboard feedback 2.5
export type OrderIssueType = 'damaged' | 'overweight' | 'repack' | 'other';

export interface OrderIssueDTO {
  id: number;
  orderId: number;
  type: OrderIssueType;
  bid?: string;
  note?: string;
  flaggedAt: number;
  flaggedBy: number;
}

export interface CreateOrderIssueRequest {
  type: OrderIssueType;
  bid?: string;
  note?: string;
}

// Vehicles + service areas — §1 Phase 2
export type VehicleClassDTO = string;

export interface VehicleDTO {
  id: number;
  partnerId: number;
  name: string;
  vehicleClass: string;
  boxCapacity?: number | null;
  weightCapacityKg?: number | null;
  isActive: boolean;
}

export interface ServiceAreaDTO {
  id: number;
  partnerId: number;
  postcodePrefix: string;
  priority: number;
}

// Route stops — §1 Phase 3+4
export type RoutePhase = 'COLLECTION' | 'DELIVERY';

export interface RouteStopDTO {
  id: number;
  orderId: number;
  driverId: number | null;
  phase: RoutePhase;
  routePosition: number;
  defaultRoutePosition: number;
  defaultRouteVersion: number;
  overridden: boolean;
  overriddenAt?: number;
  overriddenByUserId?: number;
  etaStart?: string;
  etaEnd?: string;
}

export interface RouteStopsResponse {
  stops: RouteStopDTO[];
  // Optional lock-state fields populated by the backend day-summary surface.
  // Present once Track A's auth-matrix loosening lands; treat as optional so
  // the SPA renders gracefully against either backend revision.
  lockedAt?: string;
  lockedBy?: number;
  lockedByName?: string;
}

export interface BulkReorderRouteRequest {
  driverId: number;
  phase: RoutePhase;
  stopIds: number[];
}

export interface RecomputeRouteRequest {
  driverId?: number;
  phase?: RoutePhase;
}

// Order splitting — §1 Phase 5
export interface SubOrderDTO {
  id: number;
  parentOrderId: number;
  subOrderCode: string;
  displayLabel: string;
}

export interface SubOrdersResponse {
  subOrders: SubOrderDTO[];
}

export interface SplitOrderSegment {
  bids: string[];
  driverId?: number;
}

export interface SplitOrderRequest {
  splits: SplitOrderSegment[];
}

// Scan progress — dashboard feedback 2.2
export interface StageProgress {
  scanned: number;
  total: number;
}

// Warehouse forecast — dashboard feedback 2.3
export interface WarehouseForecastCell {
  date: string;
  displayDate: string;
  dayName: string;
  inboundOrders: number;
  inboundBoxes: number;
  outboundOrders: number;
  outboundBoxes: number;
}

export interface WarehouseForecastResponse {
  cells: WarehouseForecastCell[];
}

// Drivers — dashboard feedback 1.3
export interface LogisticsDriverOption {
  id: number;
  name: string;
}

export interface LogisticsDriversResponse {
  drivers: LogisticsDriverOption[];
}
